import classNames from 'classnames';
import React, { useState } from 'react';
import type { DistributedPick } from 'type-fest';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { editorToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import type { DisplayMode } from './BannerImageList';
import BannerImageList, { BannerImageListButton } from './BannerImageList';
import * as styles from './BannerModalContent.module.scss';
import Button from './Button';
import StickyHeader from './StickyHeader';
import Tabs, { Tab } from './Tabs';
import UnsplashPicker from './UnsplashPicker';
import Uploader from './Uploader';
import type { ImageLibrary } from './imageLibraries';

const MAX_SIZE_MB = 25;

export type Banner = {
  image: FormDataEntryValue;
  unsplashId?: string;
};

type TabType = 'library' | 'upload' | 'unsplash';

interface BannerModalContentProps {
  initialTab?: TabType;
  onRemove(): void;
  onBannerChange: (newBanner: Banner | null) => void;
  imageLibrary: ImageLibrary;
  displayMode: DisplayMode;
  uploaderProps: DistributedPick<
    React.ComponentProps<typeof Uploader>,
    'info' | 'accept'
  >;
}

const BannerModalContent = ({
  onBannerChange,
  initialTab = 'library',
  onRemove,
  imageLibrary,
  displayMode,
  uploaderProps,
}: BannerModalContentProps) => {
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState<TabType>(initialTab);

  const tabs = (
    <Tabs
      right={
        <div className={styles.removeButtonContainer}>
          <Button variant="secondary" onClick={onRemove}>
            Remove
          </Button>
        </div>
      }
    >
      <Tab onClick={() => setTab('library')} active={tab === 'library'}>
        Library
      </Tab>
      <Tab onClick={() => setTab('upload')} active={tab === 'upload'}>
        Upload
      </Tab>
      <Tab onClick={() => setTab('unsplash')} active={tab === 'unsplash'}>
        Unsplash
      </Tab>
    </Tabs>
  );

  return (
    <div className={classNames(styles.container, styles[tab])}>
      {tab === 'library' && (
        <>
          <StickyHeader>
            <div className={styles.innerContent}>{tabs}</div>
          </StickyHeader>
          <div className={styles.innerContent}>
            <div className={styles.library}>
              {imageLibrary.map((libraryGroup) => (
                <div key={libraryGroup.name}>
                  <div className={styles.libraryGroupName}>
                    {libraryGroup.name}
                  </div>
                  <BannerImageList displayMode={displayMode}>
                    {libraryGroup.images.map(({ alt, image }) => (
                      <BannerImageListButton
                        key={alt}
                        onClick={() => onBannerChange({ image })}
                        alt={alt}
                        src={image}
                      />
                    ))}
                  </BannerImageList>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {tab === 'upload' && (
        <>
          <StickyHeader>
            <div className={styles.innerContent}>{tabs}</div>
          </StickyHeader>
          <div className={styles.innerContent}>
            <div className={styles.uploadContainer}>
              <Uploader
                onDrop={(files: File[]) => {
                  if (files[0]) {
                    onBannerChange({ image: files[0] });
                  }
                }}
                type="image"
                {...uploaderProps}
                maxSize={{
                  limitMB: MAX_SIZE_MB,
                  onMaxSizeError: (file) => {
                    dispatch(
                      UIActions.pushToast(
                        editorToasts.overlargeFileError({
                          fileName: file.name,
                          maxSizeMB: MAX_SIZE_MB,
                        }),
                      ),
                    );
                  },
                }}
              />
            </div>
          </div>
        </>
      )}
      {tab === 'unsplash' && (
        <UnsplashPicker.Root displayMode={displayMode} onPick={onBannerChange}>
          <StickyHeader>
            <div className={styles.innerContent}>
              {tabs}
              <UnsplashPicker.SearchForm />
            </div>
          </StickyHeader>
          <div className={styles.innerContent}>
            <UnsplashPicker.Results />
          </div>
        </UnsplashPicker.Root>
      )}
    </div>
  );
};

export default BannerModalContent;
